import {
   AdamedExpertExternalFault,
   AuthenticateWithCredentialsCommand,
   InvalidCredentialsFault,
} from '@dreryk/egabinet-adamed-expert-frontend-api';

import { dispatchSnackbarNegativeEvent, FullscreenSpinner, ModalConfirmButton } from '@dreryk/react-components';
import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { createInterpolatedTranslationKey } from '@dreryk/react-common';

import { translationKeys } from './translations';
import {
   AdamedExpertAuthenticateFormBean,
   AdamedExpertAuthenticateFormRendererProps,
} from './AdamedExpertAuthenticateForm';

type AdamedExpertAuthenticateModalConfirmButtonProps = Pick<
   AdamedExpertAuthenticateFormRendererProps,
   'handleSubmit'
> & {
   onSuccess: VoidFunction;
};

const useAdamedExpertAuthenticateFormCommand = (
   options: Pick<AdamedExpertAuthenticateModalConfirmButtonProps, 'onSuccess'>
): UseCqrsCommandResult<
   string,
   AdamedExpertAuthenticateFormBean,
   InvalidCredentialsFault | AdamedExpertExternalFault
> => {
   return useCqrsCommand<string, AdamedExpertAuthenticateFormBean, InvalidCredentialsFault | AdamedExpertExternalFault>(
      (parameters) => {
         const command = new AuthenticateWithCredentialsCommand();
         command.emailAddress = parameters.emailAddress!;
         command.password = parameters.password!;
         command.rememberMe = parameters.rememberMe;
         return command;
      },
      {
         onSuccess: (result) => {
            window.open(result, '_blank');
            options.onSuccess();
         },
         onFault: {
            'egabinet-adamed-expert-frontend-api:InvalidCredentialsFault': () => {
               dispatchSnackbarNegativeEvent(translationKeys.modal.authenticate.invalidCredentialsFault);
            },
            'egabinet-adamed-expert-frontend-api:AdamedExpertExternalFault': ({ message }) => {
               dispatchSnackbarNegativeEvent(
                  createInterpolatedTranslationKey(translationKeys.externalSystemBusinessFault, {
                     messageFromAdamedExpert: message,
                  })
               );
            },
         },
      }
   );
};

export const AdamedExpertAuthenticateModalConfirmButton = (
   props: AdamedExpertAuthenticateModalConfirmButtonProps
): React.ReactElement => {
   const { executeAsync: adamedExpertAuthenticate, loading } = useAdamedExpertAuthenticateFormCommand({
      onSuccess: props.onSuccess,
   });
   return (
      <>
         {loading ? <FullscreenSpinner label={translationKeys.loading} /> : null}
         <ModalConfirmButton
            testId='adamedExpertAuthenticate'
            label={translationKeys.modal.authenticate.logIn}
            onClick={props.handleSubmit(adamedExpertAuthenticate)}
         />
      </>
   );
};
